import LonghouseModal from "@/components/modals/LonghouseModal";
import "@/styles/markdown-editor.css";
import { gql, useMutation, useQuery } from "@apollo/client";
import { formatDistanceToNow } from "date-fns";
import "easymde/dist/easymde.min.css";
import { Clock, Search, Send } from "lucide-react";
import { useMemo, useState } from "react";
import SimpleMDE from "react-simplemde-editor";

const SEARCH_PLAYERS_QUERY = gql`
    query SearchPlayers($searchQuery: String, $limit: Int) {
        searchPlayers(searchQuery: $searchQuery, limit: $limit) {
            id
            name
            nickname
        }
    }
`;

const SEND_ADMIN_MESSAGE_MUTATION = gql`
    mutation SendAdminMessage(
        $recipientIds: [UUID!]
        $title: String!
        $message: String!
        $actionUrl: String
        $imageUrl: String
    ) {
        sendAdminMessage(
            recipientIds: $recipientIds
            title: $title
            message: $message
            actionUrl: $actionUrl
            imageUrl: $imageUrl
        ) {
            success
            count
        }
    }
`;

const ADMIN_MESSAGE_HISTORY_QUERY = gql`
    query AdminMessageHistory($limit: Int, $offset: Int) {
        adminMessageHistory(limit: $limit, offset: $offset) {
            messageId
            title
            message
            actionUrl
            imageUrl
            sender {
                id
                twitchUsername
            }
            isBroadcast
            isGroupMessage
            createdAt
            recipientCount
            readCount
            recipients {
                recipient {
                    id
                    twitchUsername
                }
                isRead
                readAt
            }
        }
    }
`;

interface Player {
    id: string;
    name: string;
    nickname: string;
}

interface MessageHistory {
    messageId: string;
    title: string;
    message: string;
    actionUrl?: string;
    imageUrl?: string;
    sender: {
        id: string;
        twitchUsername: string;
    };
    isBroadcast: boolean;
    isGroupMessage: boolean;
    createdAt: string;
    recipientCount: number;
    readCount: number;
    recipients: Array<{
        recipient: {
            id: string;
            twitchUsername: string;
        };
        isRead: boolean;
        readAt?: string;
    }>;
}

export default function AdminMessagingPage() {
    const [activeTab, setActiveTab] = useState<"send" | "history">("send");
    const [expandedMessage, setExpandedMessage] = useState<string | null>(null);
    const [messageType, setMessageType] = useState<
        "individual" | "group" | "broadcast"
    >("individual");
    const [title, setTitle] = useState("");
    const [message, setMessage] = useState("");
    const [actionUrl, setActionUrl] = useState("");
    const [imageUrl, setImageUrl] = useState("");
    const [selectedPlayers, setSelectedPlayers] = useState<Player[]>([]);
    const [playerSearch, setPlayerSearch] = useState("");

    // Modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [modalConfig, setModalConfig] = useState<{
        title: string;
        message: string;
        type: "alert" | "confirm" | "error" | "success" | "info";
    }>({
        title: "",
        message: "",
        type: "info",
    });

    const showModal = (
        title: string,
        message: string,
        type: "alert" | "confirm" | "error" | "success" | "info" = "info",
    ) => {
        setModalConfig({ title, message, type });
        setModalOpen(true);
    };

    const { data: searchData, loading: searchLoading } = useQuery<{
        searchPlayers: Player[];
    }>(SEARCH_PLAYERS_QUERY, {
        variables: { searchQuery: playerSearch, limit: 10 },
        skip: playerSearch.length < 2 || messageType === "broadcast",
    });

    const [sendMessage, { loading: sending }] = useMutation(
        SEND_ADMIN_MESSAGE_MUTATION,
        {
            onCompleted: (data) => {
                if (data.sendAdminMessage.success) {
                    showModal(
                        "Success",
                        `Message sent successfully to ${data.sendAdminMessage.count} recipient(s)!`,
                        "success",
                    );
                    setTitle("");
                    setMessage("");
                    setActionUrl("");
                    setImageUrl("");
                    setSelectedPlayers([]);
                    setPlayerSearch("");
                    // Refetch history to show the new message
                    historyRefetch();
                }
            },
            onError: (error) => {
                showModal(
                    "Error",
                    `Failed to send message: ${error.message}`,
                    "error",
                );
            },
        },
    );

    const {
        data: historyData,
        loading: historyLoading,
        refetch: historyRefetch,
    } = useQuery(ADMIN_MESSAGE_HISTORY_QUERY, {
        variables: { limit: 50 },
        skip: activeTab !== "history",
    });

    const handleSendMessage = async () => {
        if (!title.trim() || !message.trim()) {
            showModal(
                "Validation Error",
                "Please provide both a title and message.",
                "error",
            );
            return;
        }

        if (messageType !== "broadcast" && selectedPlayers.length === 0) {
            showModal(
                "Validation Error",
                "Please select at least one recipient.",
                "error",
            );
            return;
        }

        const recipientIds =
            messageType === "broadcast" ? [] : selectedPlayers.map((p) => p.id);

        await sendMessage({
            variables: {
                recipientIds,
                title,
                message,
                actionUrl: actionUrl || null,
                imageUrl: imageUrl || null,
            },
        });
    };

    const handleAddPlayer = (player: Player) => {
        if (!selectedPlayers.find((p) => p.id === player.id)) {
            setSelectedPlayers([...selectedPlayers, player]);
        }
        setPlayerSearch("");
    };

    const handleRemovePlayer = (playerId: string) => {
        setSelectedPlayers(selectedPlayers.filter((p) => p.id !== playerId));
    };

    // SimpleMDE editor options
    const editorOptions = useMemo(
        () => ({
            spellChecker: false,
            placeholder: "Enter your message using Markdown...",
            status: false,
            toolbar: [
                "bold",
                "italic",
                "heading",
                "|",
                "quote",
                "unordered-list",
                "ordered-list",
                "|",
                "link",
                "image",
                "|",
                "preview",
                "side-by-side",
                "fullscreen",
                "|",
                "guide",
            ] as any,
        }),
        [],
    );

    return (
        <div className="space-y-6">
            <div className="rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
                    Admin Messaging
                </h1>
                <p className="mt-2 text-slate-600 dark:text-slate-400">
                    Send notifications to users and view message history
                </p>

                {/* Tab Navigation */}
                <div className="mt-6 flex gap-2 border-b border-slate-300 dark:border-slate-700">
                    <button
                        onClick={() => setActiveTab("send")}
                        className={`flex items-center gap-2 px-4 py-2 font-medium transition-colors ${
                            activeTab === "send"
                                ? "border-b-2 border-blue-500 text-blue-600 dark:text-blue-400"
                                : "text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
                        }`}
                    >
                        <Send className="h-4 w-4" />
                        Send Message
                    </button>
                    <button
                        onClick={() => setActiveTab("history")}
                        className={`flex items-center gap-2 px-4 py-2 font-medium transition-colors ${
                            activeTab === "history"
                                ? "border-b-2 border-blue-500 text-blue-600 dark:text-blue-400"
                                : "text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
                        }`}
                    >
                        <Clock className="h-4 w-4" />
                        Message History
                    </button>
                </div>
            </div>

            {activeTab === "send" && (
                <div className="rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                    <div className="space-y-6">
                        {/* Message Type Selection */}
                        <div>
                            <label className="block text-sm font-medium text-slate-900 dark:text-slate-100">
                                Message Type
                            </label>
                            <div className="mt-2 flex gap-4">
                                <button
                                    onClick={() => setMessageType("individual")}
                                    className={`rounded-lg px-4 py-2 font-medium transition-colors ${
                                        messageType === "individual"
                                            ? "bg-brand text-white"
                                            : "bg-slate-200 text-slate-700 hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
                                    }`}
                                >
                                    Individual
                                </button>
                                <button
                                    onClick={() => setMessageType("group")}
                                    className={`rounded-lg px-4 py-2 font-medium transition-colors ${
                                        messageType === "group"
                                            ? "bg-brand text-white"
                                            : "bg-slate-200 text-slate-700 hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
                                    }`}
                                >
                                    Group
                                </button>
                                <button
                                    onClick={() => setMessageType("broadcast")}
                                    className={`rounded-lg px-4 py-2 font-medium transition-colors ${
                                        messageType === "broadcast"
                                            ? "bg-brand text-white"
                                            : "bg-slate-200 text-slate-700 hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
                                    }`}
                                >
                                    Broadcast to Everyone
                                </button>
                            </div>
                        </div>

                        {/* Recipient Selection (not for broadcast) */}
                        {messageType !== "broadcast" && (
                            <div>
                                <label className="block text-sm font-medium text-slate-900 dark:text-slate-100">
                                    Recipients
                                </label>
                                <div className="relative mt-2">
                                    <div className="relative">
                                        <Search className="absolute top-3 left-3 h-5 w-5 text-slate-400" />
                                        <input
                                            type="text"
                                            placeholder="Search for players..."
                                            value={playerSearch}
                                            onChange={(e) =>
                                                setPlayerSearch(e.target.value)
                                            }
                                            className="w-full rounded-lg border border-slate-300 py-2 pr-4 pl-10 dark:border-slate-600 dark:bg-slate-800 dark:text-white"
                                        />
                                    </div>

                                    {/* Search Results */}
                                    {playerSearch.length >= 2 && (
                                        <div className="absolute z-10 mt-1 w-full rounded-lg border border-slate-300 bg-white shadow-lg dark:border-slate-600 dark:bg-slate-800">
                                            {searchLoading && (
                                                <div className="p-4 text-center text-slate-600 dark:text-slate-400">
                                                    Searching...
                                                </div>
                                            )}
                                            {!searchLoading &&
                                                searchData?.searchPlayers
                                                    .length === 0 && (
                                                    <div className="p-4 text-center text-slate-600 dark:text-slate-400">
                                                        No players found
                                                    </div>
                                                )}
                                            {!searchLoading &&
                                                searchData?.searchPlayers.map(
                                                    (player) => (
                                                        <button
                                                            key={player.id}
                                                            onClick={() =>
                                                                handleAddPlayer(
                                                                    player,
                                                                )
                                                            }
                                                            className="w-full border-b border-slate-200 p-3 text-left last:border-b-0 hover:bg-slate-100 dark:border-slate-700 dark:hover:bg-slate-700"
                                                        >
                                                            <span className="font-semibold dark:text-white">
                                                                {player.name}
                                                            </span>
                                                            {player.nickname && (
                                                                <span className="ml-2 text-slate-500 dark:text-slate-400">
                                                                    (
                                                                    {
                                                                        player.nickname
                                                                    }
                                                                    )
                                                                </span>
                                                            )}
                                                        </button>
                                                    ),
                                                )}
                                        </div>
                                    )}
                                </div>

                                {/* Selected Players */}
                                {selectedPlayers.length > 0 && (
                                    <div className="mt-3 flex flex-wrap gap-2">
                                        {selectedPlayers.map((player) => (
                                            <div
                                                key={player.id}
                                                className="bg-brand flex items-center gap-2 rounded-full px-3 py-1 text-sm text-white"
                                            >
                                                <span>
                                                    {player.name}
                                                    {player.nickname &&
                                                        ` (${player.nickname})`}
                                                </span>
                                                <button
                                                    onClick={() =>
                                                        handleRemovePlayer(
                                                            player.id,
                                                        )
                                                    }
                                                    className="hover:text-slate-200"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {messageType === "broadcast" && (
                            <div className="rounded-lg bg-yellow-50 p-4 dark:bg-yellow-900/20">
                                <p className="flex items-center gap-2 text-sm font-medium text-yellow-800 dark:text-yellow-200">
                                    <svg
                                        className="h-5 w-5 shrink-0"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                        />
                                    </svg>
                                    This message will be sent to ALL active
                                    users in the system.
                                </p>
                            </div>
                        )}

                        {/* Message Title */}
                        <div>
                            <label className="block text-sm font-medium text-slate-900 dark:text-slate-100">
                                Title
                            </label>
                            <input
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="Enter message title..."
                                className="mt-2 w-full rounded-lg border border-slate-300 px-4 py-2 dark:border-slate-600 dark:bg-slate-800 dark:text-white"
                            />
                        </div>

                        {/* Message Content */}
                        <div>
                            <label className="block text-sm font-medium text-slate-900 dark:text-slate-100">
                                Message
                            </label>
                            <div className="markdown-editor mt-2">
                                <SimpleMDE
                                    value={message}
                                    onChange={setMessage}
                                    options={editorOptions}
                                />
                            </div>
                            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                                Supports Markdown formatting (bold, italic,
                                links, lists, etc.)
                            </p>
                        </div>

                        {/* Action URL */}
                        <div>
                            <label className="block text-sm font-medium text-slate-900 dark:text-slate-100">
                                Action URL (Optional)
                            </label>
                            <input
                                type="text"
                                value={actionUrl}
                                onChange={(e) => setActionUrl(e.target.value)}
                                placeholder="/dashboard/..."
                                className="mt-2 w-full rounded-lg border border-slate-300 px-4 py-2 dark:border-slate-600 dark:bg-slate-800 dark:text-white"
                            />
                            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                                Optional link to navigate users when they click
                                the notification
                            </p>
                        </div>

                        {/* Image URL */}
                        <div>
                            <label className="block text-sm font-medium text-slate-900 dark:text-slate-100">
                                Image URL (Optional)
                            </label>
                            <input
                                type="text"
                                value={imageUrl}
                                onChange={(e) => setImageUrl(e.target.value)}
                                placeholder="https://example.com/image.png"
                                className="mt-2 w-full rounded-lg border border-slate-300 px-4 py-2 dark:border-slate-600 dark:bg-slate-800 dark:text-white"
                            />
                            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                                Optional image URL to display with the
                                notification
                            </p>
                        </div>

                        {/* Send Button */}
                        <div className="flex justify-end">
                            <button
                                onClick={handleSendMessage}
                                disabled={sending}
                                className="bg-brand hover:bg-brand/90 flex items-center gap-2 rounded-lg px-6 py-3 font-medium text-white disabled:opacity-50"
                            >
                                <Send className="h-5 w-5" />
                                {sending ? "Sending..." : "Send Message"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === "history" && (
                <div className="rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                    {historyLoading ? (
                        <div className="py-12 text-center text-slate-600 dark:text-slate-400">
                            Loading message history...
                        </div>
                    ) : !historyData?.adminMessageHistory ||
                      historyData.adminMessageHistory.length === 0 ? (
                        <div className="py-12 text-center text-slate-600 dark:text-slate-400">
                            No messages sent yet
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {historyData.adminMessageHistory.map(
                                (msg: MessageHistory) => (
                                    <div
                                        key={msg.messageId}
                                        className="rounded-lg border border-slate-200 p-4 dark:border-slate-700"
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                                                    {msg.title}
                                                </h3>
                                                <p className="mt-1 line-clamp-2 text-sm text-slate-600 dark:text-slate-400">
                                                    {msg.message}
                                                </p>
                                                <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-slate-500 dark:text-slate-400">
                                                    <span>
                                                        Sent by:{" "}
                                                        {
                                                            msg.sender
                                                                .twitchUsername
                                                        }
                                                    </span>
                                                    <span>
                                                        {formatDistanceToNow(
                                                            new Date(
                                                                msg.createdAt,
                                                            ),
                                                            { addSuffix: true },
                                                        )}
                                                    </span>
                                                    <span>
                                                        {msg.recipientCount}{" "}
                                                        recipient
                                                        {msg.recipientCount !==
                                                        1
                                                            ? "s"
                                                            : ""}
                                                    </span>
                                                    <span>
                                                        {msg.readCount} read (
                                                        {Math.round(
                                                            (msg.readCount /
                                                                msg.recipientCount) *
                                                                100,
                                                        )}
                                                        %)
                                                    </span>
                                                    {msg.isBroadcast && (
                                                        <span className="rounded-full bg-purple-100 px-2 py-0.5 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                                                            Broadcast
                                                        </span>
                                                    )}
                                                    {msg.isGroupMessage && (
                                                        <span className="rounded-full bg-blue-100 px-2 py-0.5 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                                                            Group
                                                        </span>
                                                    )}
                                                </div>

                                                {/* Recipients toggle */}
                                                <button
                                                    onClick={() =>
                                                        setExpandedMessage(
                                                            expandedMessage ===
                                                                msg.messageId
                                                                ? null
                                                                : msg.messageId,
                                                        )
                                                    }
                                                    className="mt-3 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                                                >
                                                    {expandedMessage ===
                                                    msg.messageId
                                                        ? "Hide"
                                                        : "Show"}{" "}
                                                    recipients
                                                </button>

                                                {/* Recipients list */}
                                                {expandedMessage ===
                                                    msg.messageId && (
                                                    <div className="mt-3 max-h-60 overflow-y-auto rounded border border-slate-200 bg-slate-50 p-3 dark:border-slate-600 dark:bg-slate-800/50">
                                                        <div className="space-y-1">
                                                            {msg.recipients.map(
                                                                (r) => (
                                                                    <div
                                                                        key={
                                                                            r
                                                                                .recipient
                                                                                .id
                                                                        }
                                                                        className="flex items-center justify-between text-xs"
                                                                    >
                                                                        <span className="text-slate-700 dark:text-slate-300">
                                                                            {
                                                                                r
                                                                                    .recipient
                                                                                    .twitchUsername
                                                                            }
                                                                        </span>
                                                                        <span
                                                                            className={`rounded px-2 py-0.5 ${
                                                                                r.isRead
                                                                                    ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                                                                                    : "bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-400"
                                                                            }`}
                                                                        >
                                                                            {r.isRead
                                                                                ? `Read ${r.readAt ? formatDistanceToNow(new Date(r.readAt), { addSuffix: true }) : ""}`
                                                                                : "Unread"}
                                                                        </span>
                                                                    </div>
                                                                ),
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ),
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Modal */}
            <LonghouseModal
                isOpen={modalOpen}
                onClose={() => {
                    setModalOpen(false);
                }}
                title={modalConfig.title}
                message={modalConfig.message}
                type={modalConfig.type}
            />
        </div>
    );
}
